import {
    createBasketSpec,
    deleteBasketSpec,
    getBasketSpecById,
    getBasketSpecs,
    updateBasketSpec,
} from './http/basketSpecs';
import type { QueryClient, QueryObserverOptions, UseMutationOptions } from '@tanstack/react-query';
import type {
    BasketSpec,
    BasketSpecPatch,
    NewBasketSpec,
} from 'src/routes/widgets/BasketTradingWidget/state/basket-spec';

// Re-export HTTP functions so consumers don't import from queries/http/** directly
export { createBasketSpec, deleteBasketSpec, getBasketSpecById, updateBasketSpec };

export const BASKET_SPECS = 'basket-specs';

export function getBasketSpecsQuery(search?: string): QueryObserverOptions<BasketSpec[]> {
    return {
        queryKey: [BASKET_SPECS, { search }],
        queryFn: () => {
            const params: Record<string, string> = {};
            if (search) {
                params['$search'] = search;
            }
            return getBasketSpecs(params);
        },
        staleTime: 1000 * 60 * 5,
    };
}

export function getBasketSpecByIdQuery(id: string | null): QueryObserverOptions<BasketSpec | null> {
    return {
        queryKey: [BASKET_SPECS, id],
        queryFn: () => (id ? getBasketSpecById(id) : Promise.resolve(null)),
        enabled: Boolean(id),
    };
}

export function getCreateBasketSpecMutation(
    queryClient: QueryClient,
): UseMutationOptions<BasketSpec, Error, NewBasketSpec> {
    return {
        mutationFn: createBasketSpec,
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [BASKET_SPECS] });
        },
    };
}

export function getUpdateBasketSpecMutation(
    queryClient: QueryClient,
): UseMutationOptions<BasketSpec, Error, { id: string; patch: BasketSpecPatch }> {
    return {
        mutationFn: ({ id, patch }) => updateBasketSpec(id, patch),
        onSuccess: (data) => {
            queryClient.setQueryData([BASKET_SPECS, data.id], data);
            void queryClient.invalidateQueries({ queryKey: [BASKET_SPECS] });
        },
    };
}

export function getDeleteBasketSpecMutation(
    queryClient: QueryClient,
): UseMutationOptions<
    void,
    Error,
    string,
    { previousSpecsByQuery: [queryKey: readonly unknown[], data: BasketSpec[] | undefined][] }
> {
    return {
        mutationFn: deleteBasketSpec,
        onMutate: async (id) => {
            await queryClient.cancelQueries({ queryKey: [BASKET_SPECS] });
            const previousSpecsByQuery = queryClient.getQueriesData<BasketSpec[]>({
                queryKey: [BASKET_SPECS],
            });

            queryClient.setQueriesData<BasketSpec[]>({ queryKey: [BASKET_SPECS] }, (old) =>
                Array.isArray(old) ? old.filter((spec) => spec.id !== id) : old,
            );

            return { previousSpecsByQuery };
        },
        onError: (_err, _id, context) => {
            if (context?.previousSpecsByQuery) {
                for (const [queryKey, data] of context.previousSpecsByQuery) {
                    queryClient.setQueryData(queryKey, data);
                }
            }
        },
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: [BASKET_SPECS] });
        },
    };
}

export function invalidateBasketSpecsQueries(queryClient: QueryClient): void {
    void queryClient.invalidateQueries({ queryKey: [BASKET_SPECS] });
}
